import { parseCookies } from '@cian/utils/lib/shared/cookie';

export interface ISendUXSEventWhenReadyOptions {
  /** Интервал опроса готовности виджета, мс. */
  interval?: number;
  /** Максимальное время ожидания готовности, мс. */
  maxWait?: number;
  /**
   * Ключ дедупликации: событие с одним ключом уходит максимум один раз за загрузку страницы.
   * Нужен маунт-событиям (`zhk_catalog` с `action: open`) — useRef-защита в хуке сбрасывается при ремаунте
   * контейнера, дубль засчитывается бэкендом UXFB как лишний «заход», и кампании с правилом
   * «показ на 2-й заход» выезжают уже на первом.
   */
  dedupeKey?: string;
  /** Вызывается, если `window.UXS.sendEvent` бросил исключение; само исключение наружу не всплывает. */
  onError?: (error: unknown) => void;
}

const DEFAULT_INTERVAL = 500;
const DEFAULT_MAX_WAIT = 10000;

const sentDedupeKeys = new Set<string>();

/** Сбрасывает модульный дедуп по dedupeKey — только для тестов. */
export const resetUXSEventDedupe = () => {
  sentDedupeKeys.clear();
};

/**
 * cid (аналитический client id) из куки `_CIAN_GK` — общее свойство UXFB-событий с атрибутами
 * (SEL-873): web обогащает события атрибутом `cid`, аппки — `device_id`.
 */
const getCid = (): string | undefined =>
  typeof document === 'undefined' ? undefined : parseCookies(document.cookie)['_CIAN_GK'];

export const sendUXSEvent = (name: string, attributes?: Record<string, string>, onError?: (error: unknown) => void) => {
  if (typeof window === 'undefined' || !window.UXS) {
    return;
  }

  // Виджет грузится через GTM и может быть сломан или подменён. Вызов идёт синхронно из эффектов,
  // поэтому исключение здесь дошло бы до корневого error boundary и стёрло бы страницу.
  try {
    if (attributes) {
      const cid = getCid();

      window.UXS.sendEvent(name, cid ? { ...attributes, cid } : attributes);
    } else {
      window.UXS.sendEvent(name);
    }
  } catch (error) {
    onError?.(error);
  }
};

/**
 * Отправляет UXFB-событие, дождавшись готовности виджета `window.UXS` (грузится через GTM).
 * Поллит с интервалом `interval` до `maxWait`, затем молча выходит — без throw.
 */
export const sendUXSEventWhenReady = (
  name: string,
  attributes?: Record<string, string>,
  options: ISendUXSEventWhenReadyOptions = {},
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const { interval = DEFAULT_INTERVAL, maxWait = DEFAULT_MAX_WAIT, dedupeKey, onError } = options;

  if (dedupeKey) {
    if (sentDedupeKeys.has(dedupeKey)) {
      return;
    }

    sentDedupeKeys.add(dedupeKey);
  }

  if (window.UXS) {
    sendUXSEvent(name, attributes, onError);

    return;
  }

  let waited = 0;
  const timerId = setInterval(() => {
    waited += interval;

    if (window.UXS) {
      clearInterval(timerId);
      sendUXSEvent(name, attributes, onError);
    } else if (waited >= maxWait) {
      clearInterval(timerId);
    }
  }, interval);
};
